import { THANOS26_API_BASE_URL, THANOS26_API_SCOPES } from "../config";
import { Utilities } from "../Core/Utilities";
import { getThanos26Token } from "../Utils/Auth";

type Method = "GET" | "POST" | "PUT" | "DELETE";

export const MESSAGE_FORBIDDEN = "Keine Berechtigung";
export const MESSAGE_UNAUTHORIZED = "Ihre Anmeldung ist abgelaufen. Bitte melden Sie sich neu an.";

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
        this.name = "ApiError";
    }
}

/**
 * Sends a request with the Entra ID access token of the signed in user to the thanos26 API.
 * On 401 the token is refreshed once and the request is repeated.
 * @param path Route relative to the API base url, without leading slash (e.g. "manager/Settings")
 */
export async function thanos26Fetch(
    method: Method,
    path: string,
    params: Record<string, string> = {},
    body?: unknown
): Promise<Response> {
    if (!THANOS26_API_BASE_URL || THANOS26_API_SCOPES.length === 0) {
        throw new Error("thanos26 API nicht konfiguriert: REACT_APP_THANOS26_API_BASE_URL und REACT_APP_THANOS26_API_SCOPE setzen");
    }
    // e.g. a pipeline variable without "https://" or "$(THANOS26_API_BASE_URL)" when it is not defined
    if (!/^https?:\/\/[^$]+\/api\/$/i.test(THANOS26_API_BASE_URL)) {
        throw new Error(
            `thanos26 API-Adresse ungültig: "${THANOS26_API_BASE_URL}". Erwartet wird https://<host>/api/ (Pipeline-Variable THANOS26_API_BASE_URL)`
        );
    }

    // A leading slash would replace the "/api/" part of the base url
    const url = new URL(path.replace(/^\/+/, ""), THANOS26_API_BASE_URL);
    url.search = new URLSearchParams(params).toString();

    const send = async (forceRefresh: boolean) => {
        const token = await getThanos26Token(forceRefresh);
        const headers = new Headers({ Authorization: `Bearer ${token}` });
        if (body !== undefined) headers.set("Content-Type", "application/json");
        return fetch(url.href, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined
        });
    };

    let response = await send(false);
    if (response.status === 401) {
        // The token could be revoked or expired on the server side
        response = await send(true);
    }
    return response;
}

/**
 * Returns the JSON body of a thanos26 response, or throws an ApiError.
 * Auth errors are shown to the user.
 * @param forbiddenMessage Message shown on 403
 */
export async function thanos26Json(response: Response, forbiddenMessage = MESSAGE_FORBIDDEN): Promise<any> {
    if (response.status === 401) {
        Utilities.printError(MESSAGE_UNAUTHORIZED);
        throw new ApiError(response.status, MESSAGE_UNAUTHORIZED);
    }
    if (response.status === 403) {
        // Missing or rejected TimeSafe login: the backend sends { fehler, meldung }
        const message = timeSafeLoginMessage(await response.text().catch(() => "")) ?? forbiddenMessage;
        Utilities.printError(message);
        throw new ApiError(response.status, message);
    }
    if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new ApiError(response.status, errorMessage(text) || response.statusText);
    }
    if (response.status === 204) return null;
    return response.json();
}

/** Error text of a failed response: "meldung" of a JSON body ({ fehler, meldung }), otherwise the plain text */
function errorMessage(body: string): string {
    try {
        const data = JSON.parse(body);
        if (typeof data === "string") return data;
        if (data?.meldung) return data.meldung;
    } catch {
        // plain text body
    }
    return body;
}

const TIMESAFE_LOGIN_ERRORS = ["TimeSafeZugangsdatenFehlen", "TimeSafeLoginFehlgeschlagen"];

/** Message for a 403 caused by the TimeSafe login stored in the settings, otherwise undefined */
function timeSafeLoginMessage(body: string): string | undefined {
    try {
        const data = JSON.parse(body);
        if (TIMESAFE_LOGIN_ERRORS.includes(data?.fehler) && data.meldung) return data.meldung;
    } catch {
        // plain text body
    }
    return undefined;
}

export const thanos26Get = (path: string, params?: Record<string, string>) => thanos26Fetch("GET", path, params);

export const thanos26Post = (path: string, params: Record<string, string>, body: unknown) =>
    thanos26Fetch("POST", path, params, body);

export const thanos26Put = (path: string, params: Record<string, string>, body: unknown) =>
    thanos26Fetch("PUT", path, params, body);

export const thanos26Delete = (path: string, params?: Record<string, string>) =>
    thanos26Fetch("DELETE", path, params);

/** GET request returning the parsed JSON body */
export async function thanos26GetJson(path: string, params?: Record<string, string>): Promise<any> {
    return thanos26Json(await thanos26Get(path, params));
}

/** POST request returning the parsed JSON body */
export async function thanos26PostJson(path: string, params: Record<string, string>, body: unknown): Promise<any> {
    return thanos26Json(await thanos26Post(path, params, body));
}
